import { DEFAULT_PACK_MODE, DEFAULT_SHORTFALL, DEFAULT_SPRAYS } from "./bundleConfig.js";

// Pure maths for the Weerstand Volumes screen. No React in here.
// Generalised port of the "SILVALIS Volume order" tab of 2026.10.5_Bundling_Offer_V3_2.xlsx:
// any main product, any partner products, each with its own dose and number of sprays.

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));

// Round up, ignoring floating point noise (5.0000000001 must not become 6)
const roundUp = (x) => Math.ceil(x - 1e-9);

/**
 * Price the bundle uses for one pricing2 row: Chris's "Resistance Price" (pricing2.resistance_price_pv).
 * For a product without a resistance discount that column equals the list price, as in the V3 sheet.
 * If the server did not send it (older server), fall back to the Tier 1 price, then the list price.
 * The season price still caps it, as everywhere else in MyPack.
 */
export function bundlePrice(row) {
  const resistance = num(row.resistance_price_pv);
  const tier1 = num(row.tier1_price_pv);
  let price = resistance > 0 ? resistance : tier1 > 0 ? tier1 : num(row.list_price_pv);
  const season = num(row.season_price_pv);
  if (season > 0 && season < price) price = season;
  return price;
}

/** Group pricing2 rows by product; packs sorted biggest first. Rows without a list price are skipped. */
export function groupProducts(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    if (!(num(r.list_price_pv) > 0)) return;
    if (!map.has(r.product_id)) map.set(r.product_id, { productId: r.product_id, name: r.product_name, packs: [] });
    map.get(r.product_id).packs.push(r);
  });
  const out = [...map.values()];
  out.forEach((g) => g.packs.sort((a, b) => num(b.full_pack_volume) - num(a.full_pack_volume)));
  return out.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/** The "Auto" mix: as many big boxes as fit, then top up with small boxes (V3 columns Y and Z). */
export function autoMix(need, shortfall, bigVol, smallVol) {
  const minNeed = need * (1 - shortfall);
  const a = Math.floor(need / bigVol + 1e-9);
  const rest = minNeed - bigVol * a;
  const b = rest > 0 ? roundUp(rest / smallVol) : 0;
  const big = smallVol * b >= bigVol ? a + 1 : a;
  const rest2 = minNeed - big * bigVol;
  const small = rest2 > 0 ? roundUp(rest2 / smallVol) : 0;
  return { big, small };
}

/**
 * @param rows  pricing2 rows for the crop (agent prices applied by the server)
 * @param opts  {
 *   main:     { rowId, boxes, dose, sprays }          // the product that drives the hectares
 *   partners: [{ key, productId, dose, sprays, packMode: 'auto'|'large' }]
 *   shortfall: 0.03
 * }
 */
export function calcBundle(rows, opts) {
  const { main, partners = [], shortfall = 0 } = opts;
  const warnings = [];
  const byId = new Map(rows.map((r) => [r.id, r]));
  const groups = new Map(groupProducts(rows).map((g) => [g.productId, g]));

  // --- main product: boxes -> litres -> hectares
  const mainRow = main?.rowId ? byId.get(main.rowId) || null : null;
  const boxes = Math.max(0, Math.floor(num(main?.boxes)));
  const mainLitres = boxes * num(mainRow?.full_pack_volume);
  const mainPerHa = num(main?.dose) * num(main?.sprays); // L/kg per ha per season
  if (mainRow && mainPerHa <= 0) warnings.push("Hoofproduk: dosis en aantal spuite is nodig om hektaar te bereken.");
  const hectares = mainPerHa > 0 ? mainLitres / mainPerHa : 0;

  const lines = [];
  const needs = {}; // partner key -> litres/kg needed for every spray together
  const addLine = ({ key, group, label, row, boxes: n, perHa, dose = 0, sprays = 0, need = null, firstOfGroup = false }) => {
    const litres = n * num(row?.full_pack_volume);
    const price = row ? bundlePrice(row) : 0;
    lines.push({
      key,
      group,
      label,
      boxes: n,
      litres,
      listPrice: num(row?.list_price_pv),
      price,
      cost: litres * price,
      listCost: litres * num(row?.list_price_pv), // before any discount
      haCovered: perHa > 0 ? litres / perHa : 0,
      // the workings (calculation grid)
      unitVolume: num(row?.unit_volume),
      unitsPerBox: num(row?.full_pack_units),
      packVolume: num(row?.full_pack_volume),
      dose,
      sprays,
      haPerBox: perHa > 0 ? num(row?.full_pack_volume) / perHa : 0,
      need,
      firstOfGroup,
    });
  };

  if (mainRow) {
    addLine({ key: `main-${mainRow.id}`, group: "main", label: mainRow.product_pack_name || mainRow.product_name, row: mainRow, boxes, perHa: mainPerHa, dose: num(main.dose), sprays: num(main.sprays), firstOfGroup: true });
  }

  // --- partners: size each one to the hectares
  partners.forEach((p) => {
    const group = p.productId ? groups.get(p.productId) : null;
    if (!group) return;
    const perHa = num(p.dose) * num(p.sprays);
    const need = hectares * perHa;
    needs[p.key] = { perSpray: hectares * num(p.dose), total: need, sprays: num(p.sprays) };
    if (hectares > 0 && perHa <= 0) warnings.push(`${group.name}: dosis en aantal spuite is nodig.`);

    const big = group.packs[0];
    const small = group.packs.length > 1 ? group.packs[group.packs.length - 1] : null;
    let nBig = 0;
    let nSmall = 0;
    if (need > 0) {
      if (small && p.packMode === "auto") {
        ({ big: nBig, small: nSmall } = autoMix(need, shortfall, num(big.full_pack_volume), num(small.full_pack_volume)));
      } else {
        nBig = roundUp((need * (1 - shortfall)) / num(big.full_pack_volume));
      }
    }
    const label = (r) => r.product_pack_name || r.product_name;
    const w = { perHa, dose: num(p.dose), sprays: num(p.sprays), need };
    addLine({ key: `${p.key}-${big.id}`, group: p.key, label: label(big), row: big, boxes: nBig, firstOfGroup: true, ...w });
    if (small) addLine({ key: `${p.key}-${small.id}`, group: p.key, label: label(small), row: small, boxes: nSmall, ...w });
  });

  // Cover per product (all its pack sizes together) as a share of the main product's hectares
  const coverByGroup = {};
  lines.forEach((l) => {
    coverByGroup[l.group] = (coverByGroup[l.group] || 0) + l.haCovered;
  });
  lines.forEach((l) => {
    l.coverPct = hectares > 0 && l.litres > 0 ? coverByGroup[l.group] / hectares : null;
  });
  // Ratio: how many boxes of this pack cover the same hectares as ONE box of the main product; surplus hectares
  const mainHaPerBox = lines.find((l) => l.group === "main")?.haPerBox || 0;
  lines.forEach((l) => {
    l.ratio = l.haPerBox > 0 && l.group !== "main" ? mainHaPerBox / l.haPerBox : null;
    l.surplusHa = l.group !== "main" && l.boxes > 0 ? coverByGroup[l.group] - hectares : null;
  });

  const totalCost = lines.reduce((s, l) => s + l.cost, 0);
  const totalListCost = lines.reduce((s, l) => s + l.listCost, 0);
  return {
    lines,
    warnings,
    mainLitres,
    hectares,
    needs,
    totals: {
      boxes: lines.reduce((s, l) => s + l.boxes, 0),
      cost: totalCost,
      listCost: totalListCost,
      costPerHa: hectares > 0 ? totalCost / hectares : 0,
      listCostPerHa: hectares > 0 ? totalListCost / hectares : 0,
    },
  };
}

/** doseHints: productId -> dose per spray, from every bundle the server knows about. */
export function doseHintsFrom(bundles) {
  const hints = new Map();
  (Array.isArray(bundles) ? bundles : []).forEach((b) => {
    [b.main, ...(b.groups || []).flatMap((g) => g.products || [])].forEach((p) => {
      if (p && !hints.has(p.productId)) hints.set(p.productId, p.dosePerSpray);
    });
  });
  return hints;
}

/**
 * The bundle set-up for one main product: its dose and sprays, one partner per group, and the shortfall.
 * Returns { found:false } when the server has no bundle for that product (or none of its products have prices).
 */
export function bundleSetup(rows, bundles, mainProductId, makeKey = () => Math.random().toString(36).slice(2)) {
  const groups = new Map(groupProducts(rows).map((g) => [g.productId, g]));
  const list = Array.isArray(bundles) ? bundles : [];
  const bundle = list.find((b) => b.main && b.main.productId === mainProductId && groups.has(b.main.productId));
  if (!bundle) return { found: false };

  const partners = [];
  (bundle.groups || []).forEach((g) => {
    const p = (g.products || []).find((x) => groups.has(x.productId));
    if (p) partners.push({ key: makeKey(), productId: p.productId, dose: p.dosePerSpray, sprays: p.spraysPerSeason, packMode: DEFAULT_PACK_MODE });
  });

  const covers = (bundle.groups || []).map((g) => g.minCover).filter((c) => c !== null && c !== undefined);
  const shortfall = covers.length ? Math.max(0, Math.round((1 - Math.min(...covers)) * 10000) / 10000) : DEFAULT_SHORTFALL;

  return {
    found: true,
    mainRow: groups.get(bundle.main.productId).packs[0], // biggest pack
    dose: bundle.main.dosePerSpray,
    sprays: bundle.main.spraysPerSeason,
    partners,
    shortfall,
  };
}

/** What the screen shows when it opens: the first bundle the server returns (today: Silvalis). */
export function defaultSetup(rows, bundles, makeKey = () => Math.random().toString(36).slice(2)) {
  const groups = new Map(groupProducts(rows).map((g) => [g.productId, g]));
  const list = Array.isArray(bundles) ? bundles : [];
  const doseHints = doseHintsFrom(bundles);

  const first = list.find((b) => b.main && groups.has(b.main.productId));
  const setup = first ? bundleSetup(rows, bundles, first.main.productId, makeKey) : { found: false };
  if (!setup.found) {
    return { main: { rowId: null, boxes: null, dose: null, sprays: DEFAULT_SPRAYS }, partners: [], shortfall: DEFAULT_SHORTFALL, doseHints, found: false };
  }
  return {
    main: { rowId: setup.mainRow.id, boxes: 1, dose: setup.dose, sprays: setup.sprays },
    partners: setup.partners,
    shortfall: setup.shortfall,
    doseHints,
    found: true,
  };
}
